/**
 * GB組織タイプ 12分類の文言データ（正典: リポジトリ直下 GB組織タイプ12分類と文言データ.csv）
 *
 * 【これは何か】
 *  組織レポートの各節のリード文・改善提案を、12の原型ごとに用意した確定文言。
 *  従来は算出ロジックが機械的に文章を組み立てていたが、この表に置き換えることで
 *  「今月やること」まで含んだ具体的な文面を出せる。
 *
 * 【分類の考え方】
 *  思考傾向（A/B/C/D主導・X=バランス・P=分極）× 健全度（G=良好 / R=課題あり）。
 *  分極型（P-AC / P-BD）は構造的な課題そのものなので G/R の区別を持たない。
 *
 * 【文言を直すとき】
 *  CSV を直してから本ファイルを再生成するのが正。手で直す場合は CSV も合わせること。
 */

/** 12原型のID。 */
export type OrgArchetypeId =
  | 'A-G'
  | 'A-R'
  | 'B-G'
  | 'B-R'
  | 'C-G'
  | 'C-R'
  | 'D-G'
  | 'D-R'
  | 'X-G'
  | 'X-R'
  | 'P-AC'
  | 'P-BD';

/** 改善提案1件（見出し・本文・今月やること）。 */
export interface OrgArchetypeAction {
  title: string;
  body: string;
  thisMonth: string;
}

/** 1原型ぶんの文言一式。 */
export interface OrgArchetype {
  id: OrgArchetypeId;
  /** 原型名（例: 協働・良好） */
  name: string;
  /** 思考傾向の説明（例: C主導（人/共感）） */
  thinking: string;
  /** 健全度の説明（例: 良好 / つながり弱） */
  health: string;
  /** 代表プロファイル（判定条件の出典メモ） */
  profile: string;
  /** 一言キャラクター（例: 人の繋がりが強み） */
  character: string;
  /** §01 総合評価のリード文 */
  evaluationLead: string;
  /** §01/§02 組織タイプの軸ノート */
  axisNote: string;
  /** §02 社員の分布のリード文 */
  distributionLead: string;
  /** §03 つながりの構造の全社リード文 */
  connectionLead: string;
  /** §04 評価を上げるためのリード文 */
  improvementLead: string;
  /** §04 改善提案3件 */
  improvements: OrgArchetypeAction[];
  /** 改善の効果見込み（原文まま。目標スコアの根拠として保持） */
  forecast: string;
}

export const ORG_ARCHETYPES: Record<OrgArchetypeId, OrgArchetype> = {
  'A-G': {
    id: 'A-G',
    name: '論理設計・良好',
    thinking: 'A主導（仕組み/論理）',
    health: '良好',
    profile: 'A高・他中／3スコア高',
    character: '仕組みで安定して回る',
    evaluationLead:
      '全体として良好です。仕組みと段取りが整い、迷いなく物事が進む組織です。この再現性の高さを保ちつつ、人の温度感を足すとさらに強くなります。',
    axisNote:
      '「仕組み（A）」の力が高く、論理と効率で安定して回る構成です。人（C）や変化（D）は控えめな傾向です。',
    distributionLead:
      '論理・堅実寄りの分布です。処理が速く安定している一方、異なる持ち味（人・変化）を少数加えると、変化対応力が増します。',
    connectionLead:
      '相談の仕組みが機能し、属人化も抑えられている状態です。良好なつながりが再現性を支えています。',
    improvementLead:
      '良好な状態を保ちつつ、「変化への備え」と「人の温度感」を少し足すと、環境変化にも強くなります。',
    improvements: [
      {
        title: '変化への「小さな実験」枠を作る',
        body: '効率が高い反面、前例主義に傾きやすい面があります。小さく試す場を設けると硬直を防げます。',
        thisMonth: '小さな改善提案を1つ試してみる',
      },
      {
        title: '数字に「一言のねぎらい」を添える',
        body: '論理で回る組織は感情面が薄くなりがち。承認の一言が定着を支えます。',
        thisMonth: '良い成果に「助かった」と一言添える',
      },
      {
        title: '手順の「なぜ」を共有する',
        body: '仕組みが属人化した理由を言語化すると、応用が利きます。',
        thisMonth: '定番業務の「なぜこの手順か」を1つ言語化',
      },
    ],
    forecast: '良好型＝Δ小。①働きがい+4 ②総合+2 ③つながり+2',
  },
  'A-R': {
    id: 'A-R',
    name: '論理先行・連携薄',
    thinking: 'A主導',
    health: 'つながり弱',
    profile: 'A高／つながり低',
    character: '仕組みは強いが人の繋がりが薄い',
    evaluationLead:
      '一部に注意が必要な状態です。仕組みや段取りは整っている一方、「つながり」の弱さが全体の足を引っ張っています。ここの改善が、最短で組織を良くする道筋です。',
    axisNote:
      '「仕組み（A）」の力が高く、論理と効率で物事を進める構成です。反面「人（C）」の関わりが薄く、情報や相談が個人に閉じやすい傾向があります。',
    distributionLead:
      '論理・堅実タイプに寄った分布です。処理は速い一方、感情面のケアや巻き込みが手薄になりがち。人の持ち味を1〜2名加えると、connの弱さを補えます。',
    connectionLead:
      '相談が一部の人に集中し、「V字」構造が生まれやすい状態です。仕事のやり方が個人に属人化しており、その人が抜けると回らないリスクがあります。三角形を増やすことが最優先です。',
    improvementLead:
      '「つながり」を構造的に太くすることで、総合評価の底上げが見込めます。属人化の解消と、相談し合える関係づくりが鍵です。',
    improvements: [
      {
        title: '「V字」を「三角形」に変える',
        body: '相談が特定の人に集中しています。仕事のやり方をもう1人に共有し、3人で相談し合える三角形を作ると、構造的に安定します。',
        thisMonth: '頼られている人の仕事のやり方を、メモで1つだけ書き出してもらう',
      },
      {
        title: '情報を「open」にする仕組みを1つ',
        body: '効率を優先するあまり、情報が個人に閉じがちです。共有の場を1つ設けるだけで、連携の質が上がります。',
        thisMonth: '週1回15分、進捗を全員で口頭共有する時間を作る',
      },
      {
        title: '「人」の視点を意思決定に入れる',
        body: '論理だけで決めると、現場の納得が置き去りになりがちです。決定前に一言、現場の声を聞く手順を挟みます。',
        thisMonth: '次の決定の前に、影響を受ける人に1人だけ意見を聞く',
      },
    ],
    forecast: '①つながり+13 ②つながり+6・働きがい+3 ③働きがい+4（弱点=つながりへの施策Δ大）',
  },
  'B-G': {
    id: 'B-G',
    name: '堅実運用・良好',
    thinking: 'B主導（安定/秩序）',
    health: '良好',
    profile: 'B高／3スコア高',
    character: '着実に回る安定現場',
    evaluationLead:
      '全体として良好です。着実さと安定感が組織の土台になっており、大きなブレなく回っています。この安定を活かしつつ、前進の一歩を意識するとさらに良くなります。',
    axisNote:
      '「安定・秩序（B）」の力が高く、ルールと継続で回る構成です。変化（D）や発想は控えめな傾向です。',
    distributionLead:
      '堅実タイプ中心の分布です。ばらつきが少なくまとまりが良い一方、変化・発想の持ち味を少数加えると、停滞を防げます。',
    connectionLead:
      '決まった関係の中で安定して相談でき、離職の兆しも低めです。安定した人間関係が強みです。',
    improvementLead:
      '安定を保ちつつ「少しの変化」と「成長の実感」を足すと、守りだけでなく前進もできる組織になります。',
    improvements: [
      {
        title: '「変える枠」を月1つだけ作る',
        body: '安定志向が強いと変化が止まりやすい。あえて変える対象を1つ決めます。',
        thisMonth: 'やめても困らない作業を1つ探して止める',
      },
      {
        title: '若手に「小さな裁量」を渡す',
        body: '秩序が強いと成長実感が乏しくなりがち。小さな判断を任せると働きがいが上がります。',
        thisMonth: '承認を1つ、部下に任せてみる',
      },
      {
        title: '「うまくいっている型」を横展開する',
        body: '堅実な良い型を他部署に広げると、組織全体が底上げされます。',
        thisMonth: 'うまくいっている手順を1つ他チームに共有',
      },
    ],
    forecast: '良好型＝Δ小。①働きがい+3 ②総合+2 ③つながり+2',
  },
  'B-R': {
    id: 'B-R',
    name: '安定過多・停滞',
    thinking: 'B主導',
    health: '働きがい低',
    profile: 'B高／働きがい低',
    character: '安定しすぎて成長実感が乏しい',
    evaluationLead:
      '一部に注意が必要です。安定して回っている一方、「働きがい」の低さが気がかりです。変化と成長の機会を意図的に作ることが、改善の近道です。',
    axisNote:
      '「安定・秩序（B）」が高く、変化（D）が薄い構成です。守りは強いが、新しさや刺激が不足しがちです。',
    distributionLead:
      '堅実タイプに強く偏った分布です。まとまりはあるが、変化を起こす持ち味がほぼ不在。多様性の注入が課題です。',
    connectionLead: '関係は安定していますが、予定調和で新しい相談や挑戦が生まれにくい状態です。',
    improvementLead:
      '「働きがい」を上げるには、小さな変化・裁量・成長実感の3点。守りを崩さず前進の仕掛けを入れます。',
    improvements: [
      {
        title: '若手の「成長実感」の場を月1回作る',
        body: '安定組織は学びが止まりがち。学びの場が定着と働きがいを支えます。',
        thisMonth: '入社2〜3年目に「最近学んだこと」を聞く',
      },
      {
        title: '「変える対象」を全員で1つ決める',
        body: '停滞は変化の欠如から。小さな変化を全員で選ぶと当事者意識が生まれます。',
        thisMonth: 'やめる/変える作業を全員で1つ決める',
      },
      {
        title: '裁量を1段おろす',
        body: '判断が上に集中すると成長機会が失われます。小さな権限委譲が効きます。',
        thisMonth: '定例の判断を1つ部下に任せる',
      },
    ],
    forecast: '①働きがい+11 ②働きがい+5・つながり+3 ③働きがい+4（弱点=働きがいへの施策Δ大）',
  },
  'C-G': {
    id: 'C-G',
    name: '協働・良好',
    thinking: 'C主導（人/共感）',
    health: '良好',
    profile: 'C高／つながり高',
    character: '人の繋がりが強み',
    evaluationLead:
      '全体として良好な状態です。人を支え合う土壌があり、現場のまとまりが強みになっています。この協働の力を言語化して共有すると、さらに安定します。',
    axisNote:
      '「人（C）」の力が組織全体で高く、気配りと巻き込みで物事が進む構成です。一方で仕組み化（A）や決断の速さ（D）は控えめな傾向です。',
    distributionLead:
      '多くのメンバーが「調和・協働」寄りに分布しています。似た持ち味が集まりまとまりやすい反面、異なる視点（変化・論理）を意図的に取り入れると、視野が広がります。',
    connectionLead:
      '相談し合える関係が広く築けており、誰かが抜けてもカバーし合える三角形が多い状態です。この健全なつながりが、働きがいと定着を下支えしています。',
    improvementLead:
      '現在の良好な状態を保ちつつ、「強みの言語化」と「新しい視点の取り込み」に取り組むと、さらに一段上の組織になります。',
    improvements: [
      {
        title: '協働の強みを「言葉」にして共有する',
        body: 'うまくいっている助け合いは、往々にして無意識です。何が機能しているかを言語化すると、再現性が上がり新メンバーにも伝わります。',
        thisMonth: '「最近助かった」出来事を、朝礼で1つだけ共有してみる',
      },
      {
        title: '異なる視点を1人、意図的に招く',
        body: '同質な良さの反面、変化や論理の視点が薄くなりがちです。会議に「あえて違う持ち味」の人を入れると、盲点が減ります。',
        thisMonth: '次の企画に、普段と違うタイプの人を1人アサインする',
      },
      {
        title: '決めごとの「締め切り」を1つ置く',
        body: 'まとまりを重んじるあまり決断が遅れることがあります。小さな決定に期限を設けると、協働の良さを保ったまま前に進みます。',
        thisMonth: '迷っている案件を1つ選び、「今週中に決める」と宣言する',
      },
    ],
    forecast: '良好型＝Δ小。①働きがい+3 ②総合バランス+2 ③つながり+2',
  },
  'C-R': {
    id: 'C-R',
    name: '情に偏り・集中',
    thinking: 'C主導',
    health: '一部集中/離職注意',
    profile: 'C高／つながり中・離職注意',
    character: 'まとまるが一部に負荷集中',
    evaluationLead:
      '一部に注意が必要です。人のつながりは強い一方、特定の人に相談や負担が集中し、離職の兆しが見え始めています。負荷の分散が改善の近道です。',
    axisNote:
      '「人（C）」の力が高く、協調で回る構成です。一方で仕組み化（A）が薄く、頼れる人に負担が偏りやすい傾向があります。',
    distributionLead:
      '協調タイプ中心の分布です。まとまりは良いが、支える側の少数に負荷が集中する構造が見えます。',
    connectionLead:
      '相談は活発ですが「頼られる人」への集中（V字）が強く、その人の疲弊と離職リスクが懸念されます。',
    improvementLead:
      'つながりの強みを保ちつつ、「負荷の分散」と「支える人のケア」で、離職リスクを下げられます。',
    improvements: [
      {
        title: '「頼られる人」の仕事を可視化して分散する',
        body: '善意の集中は疲弊を招きます。役割を書き出し2人以上に分けます。',
        thisMonth: '頼られている人の担当を1つ棚卸しする',
      },
      {
        title: '支える人を「支える」仕組みを作る',
        body: 'ケアする側がケアされない状態は危険。感謝と休息を意図的に。',
        thisMonth: '負荷が高い人に「無理ない?」と声をかける',
      },
      {
        title: '断れる文化を作る',
        body: '協調が強いと断れず抱え込みがち。上長が率先して「任せていい」と示します。',
        thisMonth: '抱え込んでいる仕事を1つ、上長が引き取る',
      },
    ],
    forecast: '①つながり+8・離職+6 ②離職+5 ③働きがい+4（離職・つながりへの施策Δ大）',
  },
  'D-G': {
    id: 'D-G',
    name: '変革推進・良好',
    thinking: 'D主導（変化/創造）',
    health: '良好',
    profile: 'D高／3スコア高',
    character: '発想と勢いがある',
    evaluationLead:
      '全体として良好です。発想力と推進力があり、変化を前向きに起こせる組織です。この勢いを保ちつつ、定着と土台づくりを意識するとさらに強くなります。',
    axisNote:
      '「変化・創造（D）」の力が高く、発想とスピードで動く構成です。安定（B）や仕組み化（A）は控えめな傾向です。',
    distributionLead:
      '変化・発想寄りの分布です。勢いがある一方、着実に支える持ち味を少数加えると、変化が定着しやすくなります。',
    connectionLead:
      '挑戦を後押しし合う関係が築けており、活気があります。良好なつながりが挑戦を支えています。',
    improvementLead:
      '良好な勢いを保ちつつ、「定着の仕組み」と「振り返り」を足すと、変化が成果として積み上がります。',
    improvements: [
      {
        title: '変化を「定着」させる仕組みを1つ',
        body: '始めるのは得意でも続ける仕組みが弱い。定着の担当を決めます。',
        thisMonth: '始めた取り組みの「続ける担当」を1人決める',
      },
      {
        title: '振り返りの場を月1回作る',
        body: '勢い任せは学びが流れます。短い振り返りで成功要因を残します。',
        thisMonth: '終わった案件を15分で振り返る',
      },
      {
        title: '着実タイプを1人巻き込む',
        body: '変化を支える土台役がいると、挑戦が空中分解しません。',
        thisMonth: '次の企画に堅実タイプを1人加える',
      },
    ],
    forecast: '良好型＝Δ小。①働きがい+4 ②総合+2 ③つながり+2',
  },
  'D-R': {
    id: 'D-R',
    name: '発散・定着リスク',
    thinking: 'D主導',
    health: '定着リスク',
    profile: 'D高／離職リスク高',
    character: '変化は多いが人が定着しない',
    evaluationLead:
      '注意が必要です。変化と挑戦は活発な一方、「離職の兆し」が高く、人が定着しにくい状態です。土台と一貫性を作ることが、改善の近道です。',
    axisNote:
      '「変化・創造（D）」が高く、安定（B）が薄い構成です。動きは速いが、拠り所や継続性が不足しがちです。',
    distributionLead:
      '発想・変化に強く偏った分布です。刺激は多いが、支える土台役がほぼ不在。安定の注入が課題です。',
    connectionLead:
      '変化が速く関係も流動的で、腰を据えた相談や定着が生まれにくい状態です。これが離職リスクの背景です。',
    improvementLead:
      '「離職の兆し」を下げるには、一貫性・拠り所・成長の道筋。勢いを止めず土台を足します。',
    improvements: [
      {
        title: '「変えないこと」を1つ決める',
        body: 'すべてが変わると疲れます。守る軸を1つ明示すると安心感が生まれます。',
        thisMonth: '「これは変えない」という方針を1つ言語化',
      },
      {
        title: '一人ひとりの「次の道筋」を示す',
        body: '変化が速いとキャリアが見えず離職に。短くても道筋を対話します。',
        thisMonth: '気になるメンバー1人と将来を5分話す',
      },
      {
        title: '着実に支える人を評価する',
        body: '派手な変化だけでなく、支える役を認めると定着が上がります。',
        thisMonth: '地道に支えている人を1人、名前を挙げて労う',
      },
    ],
    forecast: '①離職+12 ②離職+6・働きがい+3 ③離職+5（弱点=離職への施策Δ大）',
  },
  'X-G': {
    id: 'X-G',
    name: '多様が噛み合う・良好',
    thinking: 'バランス（中央厚）',
    health: '良好',
    profile: '均等／3スコア高',
    character: '多様なタイプが機能',
    evaluationLead:
      '全体として良好です。多様な持ち味がバランスよく噛み合い、状況に応じて力を発揮できる組織です。この多様性を活かす運用を続けると、変化にも強くなります。',
    axisNote:
      '4つの持ち味（仕組み・安定・人・変化）が偏りなく揃った構成です。特定タイプに依存せず、状況適応力が高いです。',
    distributionLead:
      '中央に程よく散らばった分布です。多様性が分断ではなく協働として機能している良い状態です。',
    connectionLead:
      '立場をまたいだ相談も起きており、異なる持ち味が橋渡しされています。健全なつながりです。',
    improvementLead:
      '良好な多様性を活かすには、「役割の明確化」と「多様性を意図的に使う」運用。埋もれた強みを引き出します。',
    improvements: [
      {
        title: '案件ごとに「持ち味の配役」を意識する',
        body: '多様性は使わなければ宝の持ち腐れ。役割を持ち味に合わせます。',
        thisMonth: '次の仕事で「誰の持ち味が活きるか」を一言決める',
      },
      {
        title: '少数意見を拾う仕組みを作る',
        body: 'バランス型は平均に流れやすい。あえて異なる声を拾います。',
        thisMonth: '会議で発言の少ない人に1人聞く',
      },
      {
        title: '強みの「見える化」をする',
        body: '多様だと個々の強みが埋もれます。棚卸しで活用度が上がります。',
        thisMonth: 'メンバーの強みを1人1つ書き出す',
      },
    ],
    forecast: '良好型＝Δ小。①働きがい+3 ②総合+3 ③つながり+2',
  },
  'X-R': {
    id: 'X-R',
    name: '無難・強み不明瞭',
    thinking: 'バランス',
    health: '働きがい中庸',
    profile: '均等／働きがい中庸',
    character: 'まとまりはあるが突出がない',
    evaluationLead:
      '一部に注意が必要です。大きな弱点はない一方、「これが強み」と言える突出も乏しく、働きがいが中庸にとどまっています。組織の「らしさ」を作ることが改善の近道です。',
    axisNote:
      '4つの持ち味が均等な構成です。バランスは良いが、方向性や強みが定まりにくい面があります。',
    distributionLead:
      '中央に集まった分布です。無難でまとまりはあるが、際立った特徴に欠けます。強みの言語化が課題です。',
    connectionLead:
      '関係は安定していますが、共通の目的や熱量が薄く、相談も予定調和に留まりがちです。',
    improvementLead:
      '「働きがい」を上げるには、組織の強み・目的・らしさの明確化。無難さを「意図した強み」に変えます。',
    improvements: [
      {
        title: '組織の「勝ちパターン」を1つ言語化する',
        body: '強みが曖昧だと誇りが持てません。得意な型を言葉にします。',
        thisMonth: '「うちが得意なこと」を全員で1つ挙げる',
      },
      {
        title: '共通の目的を掲げ直す',
        body: '熱量不足は目的の曖昧さから。全員が向く先を再確認します。',
        thisMonth: '「何のために働くか」を1文で共有',
      },
      {
        title: '一人ひとりの持ち味を「役割」にする',
        body: '均質だと個が埋もれます。持ち味を役割に結ぶと働きがいが出ます。',
        thisMonth: '各人に「あなたの役割は〜」と1つ伝える',
      },
    ],
    forecast: '①働きがい+9 ②働きがい+4・つながり+3 ③総合+3',
  },
  'P-AC': {
    id: 'P-AC',
    name: 'しくみ↔ひと 分裂',
    thinking: '分極（A↔C）',
    health: 'つながり弱（分裂）',
    profile: 'A・C高／B・D低・分極高',
    character: '効率派と人情派が噛み合わない',
    evaluationLead:
      '注意が必要な状態です。組織の中に「効率・仕組み重視」と「人・協調重視」の2つの流れがあり、噛み合わずにすれ違いが起きやすくなっています。両者をつなぐ「通訳役」が鍵です。',
    axisNote:
      '「仕組み（A）」と「人（C）」の両極が強く、その中間（安定・変化）が薄い構成です。平均では見えにくいですが、内部は2つの立場に割れている可能性があります。',
    distributionLead:
      '散布図が左上（論理）と右下（協調）の2カ所に割れています。これは多様性ではなく「分断」に近い状態。両者を橋渡しできる中間タイプの存在が、組織の安定を左右します。',
    connectionLead:
      '同じ立場の中では相談し合えても、立場をまたぐ相談が起きにくい状態です。「効率派」と「人情派」の間に見えない壁があり、これが連携の弱さの正体です。壁を越える通訳役を育てることが最優先です。',
    improvementLead:
      '2つの立場を「対立」から「補完」に変えることで、つながりと総合評価の改善が見込めます。橋渡し人材の育成と、共通言語づくりが鍵です。',
    improvements: [
      {
        title: '「通訳役（橋渡し）」を意図的に作る',
        body: '効率派と人情派の両方の言葉が分かる人を、意図的に間に置きます。この1人がいるだけで、すれ違いが激減します。',
        thisMonth: '両方と話せそうな人を1人選び、橋渡し役を頼んでみる',
      },
      {
        title: '立場をまたぐ「共通の目的」を1つ掲げる',
        body: '手段（効率か人か）で割れているときは、目的に立ち返ると一致点が見えます。全員が賛成できるゴールを1つ言語化します。',
        thisMonth: '「何のためにやるか」を全員で1文にまとめる場を持つ',
      },
      {
        title: '混成チームで小さな成功体験を作る',
        body: '両方の立場が入った小さなプロジェクトで成功を積むと、「一緒にやれる」感覚が生まれ、壁が下がります。',
        thisMonth: '効率派と人情派を1人ずつ組ませた小さな仕事を1つ作る',
      },
    ],
    forecast:
      '①つながり+12 ②つながり+5・働きがい+4 ③つながり+6（分極型は橋渡し施策Δ大／緩衝力Big5高で上振れ）',
  },
  'P-BD': {
    id: 'P-BD',
    name: '守る↔変える 対立',
    thinking: '分極（B↔D）',
    health: 'つながり弱（対立）',
    profile: 'B・D高／A・C低・分極高',
    character: '守旧派と改革派が対立',
    evaluationLead:
      '注意が必要です。組織の中に「今を守りたい」流れと「変えたい」流れがあり、対立してブレーキとアクセルを同時に踏む状態になっています。両者の合意形成が鍵です。',
    axisNote:
      '「安定（B）」と「変化（D）」の両極が強く、中間（仕組み・人）が薄い構成です。平均では見えにくいですが、方針で割れている可能性があります。',
    distributionLead:
      '散布図が左下（安定）と右上（変化）の2カ所に割れています。多様性ではなく方針の分断に近い状態。合意形成の場が安定を左右します。',
    connectionLead:
      '同じ立場の中では相談できても、方針をまたぐ対話が起きにくい状態です。守旧派と改革派の間の溝が、連携を弱めています。',
    improvementLead:
      '「対立」を「役割分担」に変えることで、つながりと推進力の両立が見込めます。合意形成の場と、変化の進め方の設計が鍵です。',
    improvements: [
      {
        title: '「守る/変える」の役割分担を明文化する',
        body: '対立は役割の未整理から。守る人・変える人の担当を決めると噛み合います。',
        thisMonth: '「守る領域/変える領域」を1つずつ決める',
      },
      {
        title: '変化を「小さく試す」ルールを作る',
        body: '全面対決を避け、小さく試して判断する枠を作ると合意しやすくなります。',
        thisMonth: '変えたい案を1つ、期間限定で試す',
      },
      {
        title: '双方が入る合意形成の場を持つ',
        body: '方針は片方だけで決めると溝が深まります。両者が入る場で決めます。',
        thisMonth: '守る派・変える派が同席する場を1回作る',
      },
    ],
    forecast: '①つながり+11 ②つながり+5・働きがい+4 ③つながり+6（分極型は合意形成施策Δ大）',
  },
};

// ===== 判定ロジック =====
//
// 【重要・暫定値】以下の閾値はまだ実測データが無いため、統計的な性質から導いた暫定値。
// 実際の顧客データが貯まったら、12分類の出現割合を見てここだけを調整すること
// （判定の呼び出し側やレポート側を触る必要はない）。

/**
 * 健全度 G（良好）の判定ライン。3スコアすべてがこの点数以上なら G、1つでも下回れば R。
 *
 * 60点にした理由:
 *  - orgScore.ts の既存グレードが 80/60/40（A/B/C/D）で、60は「B以上」の境界。
 *    新しい基準を持ち込まずに「良好=3スコアすべてB以上」と説明できる。
 *  - 50点は回答平均3.0（＝どちらでもない）であり「良好」とは呼べない。
 *  - シミュレーション上、G が約3割・R が約7割となり、両方の文言が実際に使われる。
 */
export const HEALTHY_SCORE_MIN = 60;

/**
 * 「バランス型(X)」と判定する4軸スプレッド(max−min)の上限。
 * 「分極型(P)」と判定する対角ギャップの下限。
 *
 * いずれも回答人数で補正する。集約4軸は社員の平均なので、
 * 人数が少ないほど測定ノイズでスプレッドが大きく出る（√人数に反比例）。
 * 固定値だと、同じ体質でも 5名の会社は「尖った組織」、100名の会社は「バランス型」
 * と判定されてしまい、会社間で比較できない。
 *   実測: 5名=スプレッド中央4.80 / 20名=2.55 / 100名=1.46
 */
export const BALANCE_BASE = 1.2;
export const BALANCE_NOISE = 6.0;
export const POLAR_BASE = 1.0;
export const POLAR_NOISE = 3.0;

/** 回答人数に応じたバランス型の判定上限。 */
export function balancedSpreadLimit(memberCount: number): number {
  return BALANCE_BASE + BALANCE_NOISE / Math.sqrt(Math.max(1, memberCount));
}

/** 回答人数に応じた分極型の判定下限。 */
export function polarGapLimit(memberCount: number): number {
  return POLAR_BASE + POLAR_NOISE / Math.sqrt(Math.max(1, memberCount));
}

/** 判定に使う集約4軸（0〜12、合計24）。 */
export interface OrgArchetypeAxis {
  A: number;
  B: number;
  C: number;
  D: number;
}

/** 判定に使う3スコア（0〜100）。 */
export interface OrgArchetypeScores {
  wellbeing: number;
  connection: number;
  retention: number;
}

export interface OrgArchetypeResult {
  archetype: OrgArchetype;
  /** 判定根拠（デバッグ・注記用） */
  rationale: string;
}

/**
 * 集約4軸・3スコア・回答人数から12原型のいずれかへ分類する。
 *
 * 判定順:
 *   1. 分極（A・C が高く B・D が低い / その逆）→ P-AC / P-BD（G/R の区別なし）
 *   2. 4軸の差が小さい → X（バランス）
 *   3. それ以外 → 最も高い軸で A / B / C / D
 *   4. 1以外は健全度を付与: 3スコアすべて HEALTHY_SCORE_MIN 以上 → G、それ以外 → R
 */
export function classifyOrgArchetype(
  axis: OrgArchetypeAxis,
  scores: OrgArchetypeScores,
  memberCount: number
): OrgArchetypeResult {
  const { A, B, C, D } = axis;
  const gapAC = Math.min(A, C) - Math.max(B, D);
  const gapBD = Math.min(B, D) - Math.max(A, C);
  const polarLimit = polarGapLimit(memberCount);

  if (gapAC >= polarLimit || gapBD >= polarLimit) {
    const id: OrgArchetypeId = gapAC >= gapBD ? 'P-AC' : 'P-BD';
    return {
      archetype: ORG_ARCHETYPES[id],
      rationale: `分極（ギャップ ${Math.max(gapAC, gapBD).toFixed(1)} ≧ ${polarLimit.toFixed(1)}）`,
    };
  }

  const values = [A, B, C, D];
  const spread = Math.max(...values) - Math.min(...values);
  const spreadLimit = balancedSpreadLimit(memberCount);

  const healthy =
    scores.wellbeing >= HEALTHY_SCORE_MIN &&
    scores.connection >= HEALTHY_SCORE_MIN &&
    scores.retention >= HEALTHY_SCORE_MIN;
  const suffix = healthy ? 'G' : 'R';

  if (spread < spreadLimit) {
    const id = `X-${suffix}` as OrgArchetypeId;
    return {
      archetype: ORG_ARCHETYPES[id],
      rationale: `バランス（スプレッド ${spread.toFixed(1)} < ${spreadLimit.toFixed(1)}）／健全度${suffix}`,
    };
  }

  const dominant = (['A', 'B', 'C', 'D'] as const).reduce((best, k) =>
    axis[k] > axis[best] ? k : best
  );
  const id = `${dominant}-${suffix}` as OrgArchetypeId;
  return {
    archetype: ORG_ARCHETYPES[id],
    rationale: `${dominant}主導（スプレッド ${spread.toFixed(1)}）／健全度${suffix}`,
  };
}
